import { RefreshIcon } from "@hugeicons-pro/core-stroke-rounded";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import moment from "moment";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import { Card } from "shared/components/Card";

// For AI
import "./styles.css";

import { ApplicationIntelligenceError } from "./slices/Error";
import { ApplicationIntelligenceNotAvailableForThisApplication } from "./slices/NotAvailable";
import { ApplicationIntelligenceLoading } from "./slices/Loading";
import { Feedback, HighlightsSummary } from "./types";
import { ApplicationIntelligenceSuccess } from "./slices/Success";
import { Applicant } from "types/application";

const ApplicationIntelligenceOverviewContent = ({
  isLoading,
  isError,
  data,
  feedback,
  applicationId,
  applicants,
  hasSubmittedApplicants
}: {
  isLoading: boolean;
  isError?: boolean;
  data?: HighlightsSummary;
  feedback?: Feedback;
  applicationId: number;
  applicants: Applicant[];
  hasSubmittedApplicants?: boolean;
}) => {
  if (!hasSubmittedApplicants)
    return <ApplicationIntelligenceNotAvailableForThisApplication />;

  if (isLoading) return <ApplicationIntelligenceLoading />;
  if (isError) return <ApplicationIntelligenceError />;
  if (data)
    return (
      <ApplicationIntelligenceSuccess
        {...data}
        feedback={feedback}
        applicationId={applicationId}
        applicants={applicants}
      />
    );
};

export const ApplicationIntelligenceOverview = ({
  applicationId,
  applicants,
  hasSubmittedApplicants = false
}: {
  applicationId: number;
  applicants: Applicant[];
  hasSubmittedApplicants?: boolean;
}) => {
  const queryClient = useQueryClient();
  const ai = useQuery({
    queryKey: ["APPLICATION_INTELLIGENCE_OVERVIEW", applicationId],
    queryFn: () => {
      return instance
        .get<HighlightsSummary>(
          `/portal/v1/applications/${applicationId}/application_intelligence/highlights`
        )
        .then((response) => response.data);
    },
    enabled: Boolean(applicationId) && hasSubmittedApplicants
  });

  const feedback = useQuery({
    queryKey: ["APPLICATION_INTELLIGENCE_FEEDBACK", applicationId],
    queryFn: () => {
      return instance
        .get<Feedback>(
          `/portal/v1/applications/${applicationId}/application_intelligence/feedback`
        )
        .then((response) => response.data);
    },
    enabled: Boolean(applicationId) && Boolean(ai.data?.id)
  });

  const refreshMutation = useMutation({
    mutationKey: ["APPLICATION_INTELLIGENCE_REFRESH", applicationId],
    mutationFn: async () =>
      instance
        .post(
          `/portal/v1/applications/${applicationId}/application_intelligence/refresh`
        )
        .then((response) => {
          return response.data;
        })
  });

  return (
    <Card className="max-w-[1500px]">
      <header className="h-14 flex justify-between gap-2 px-6 border-b border-alpha-40">
        <div className="flex gap-2 items-center">
          <h1 className="text-base font-medium text-content-700">
            Application Intelligence
          </h1>

          <Badge size={BadgeSize.BASE} variant={BadgeVariant.BASE}>
            Beta
          </Badge>
        </div>

        <div className="flex gap-2.5 items-center">
          {hasSubmittedApplicants ? (
            <IconButton
              variant={IconButtonVariant.TEXT}
              size={IconButtonSize.SM}
              HugeIcon={RefreshIcon}
              onClick={() => {
                refreshMutation.mutateAsync().then(() => {
                  queryClient.invalidateQueries({
                    queryKey: [
                      "APPLICATION_INTELLIGENCE_OVERVIEW",
                      applicationId
                    ]
                  });
                  ai.refetch();
                });
              }}
              loading={refreshMutation.isPending}
              className="!text-content-1000 hover:!text-content-700 hover:rotate-[360deg] duration-300 transition-transform"
            />
          ) : null}

          {ai.data?.generated_at && moment(ai.data.generated_at).isValid() ? (
            <p className="text-sm font-medium text-content-1000">
              Last update:{" "}
              {moment(ai.data.generated_at).format("MMM d, yyyy [at] h:mm a")}
            </p>
          ) : null}
        </div>
      </header>
      <ApplicationIntelligenceOverviewContent
        isLoading={ai.isLoading || ai.isFetching || refreshMutation.isPending}
        isError={ai.isError}
        data={ai.data}
        feedback={feedback.data}
        applicationId={applicationId}
        applicants={applicants}
        hasSubmittedApplicants={hasSubmittedApplicants}
      />
    </Card>
  );
};
